import { Box, Flex, HStack, IconButton } from "@chakra-ui/react"
import { EntityId } from "@jackfruit/common"
import { clamp } from "lodash"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { FiMinus, FiPlus } from "react-icons/fi"
import { useLineItem, useLineItemActions } from "~/hooks/useLineItem"
import { useProcessActions } from "~/hooks/useProcessActions"
import { interactionResponse } from "~/services/webvitals/defer"

interface Props {
  lineItemId: EntityId
  lineItemQuantityLimit: number
}

const LineItemPreviewQuantitySelectorBasic: React.FC<Props> = ({
  lineItemId,
  lineItemQuantityLimit,
}) => {
  const { lineItem } = useLineItem(lineItemId)

  const { t } = useTranslation()
  const process = useProcessActions()
  const { updateQuantity } = useLineItemActions()

  const handleQuantityChange = useCallback(
    async (newQuantity: number) => {
      await interactionResponse()
      const clampedQuantity = clamp(newQuantity, 1, lineItemQuantityLimit)
      const goUp = clampedQuantity > lineItem.quantity

      if (lineItem.quantity !== newQuantity) {
        process.fireAnalyticEvent({
          eventType: goUp ? "addOneItemToCart" : "removeOneItemFromCart",
          data: { lineItemId },
        })
      }

      updateQuantity(lineItemId, clampedQuantity)
    },
    [
      lineItem.quantity,
      lineItemId,
      lineItemQuantityLimit,
      process,
      updateQuantity,
    ]
  )

  const handleQuantityDec = useCallback(
    () => handleQuantityChange(lineItem.quantity - 1),
    [handleQuantityChange, lineItem.quantity]
  )

  const handleQuantityInc = useCallback(
    () => handleQuantityChange(lineItem.quantity + 1),
    [handleQuantityChange, lineItem.quantity]
  )

  return (
    <Flex px={3.5} alignItems="center" justifyContent="space-between" flex={1}>
      <HStack spacing={0}>
        <IconButton
          size="sm"
          rounded="full"
          aria-label={t("components.cart.LineItemPreview.remove")}
          icon={<FiMinus />}
          data-testid={`lineItemPreviewRemoveOneBtn-${lineItem.productId}`}
          className="decLineItemQuantity"
          onClick={handleQuantityDec}
        />
        <Box
          textAlign="center"
          width="3rem"
          data-testid={`lineItemPreviewQuality-${lineItem.productId}`}
          aria-live="polite"
        >
          {lineItem.quantity}
        </Box>
        <IconButton
          size="sm"
          rounded="full"
          data-testid={`lineItemPreviewAddoneBtn-${lineItem.productId}`}
          aria-label={t("components.cart.LineItemPreview.add")}
          icon={<FiPlus />}
          className="incLineItemQuantity"
          onClick={handleQuantityInc}
          disabled={lineItem.quantity >= lineItemQuantityLimit}
        />
      </HStack>
    </Flex>
  )
}

export default LineItemPreviewQuantitySelectorBasic
